import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../App";
import { useTheme } from "../../context/ThemeContext";
import { TIER_META, tierColor, formatCurrency, formatDate } from "./format";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Billing" },
  { key: "ready", label: "Ready to bill" },
  { key: "signed", label: "Signed" },
  { key: "forecasted", label: "Forecasted" },
  { key: "other", label: "Paused & ended" },
];
const OTHER = ["paused", "completed", "cancelled"];

function TierBadge({ tier }) {
  const { theme } = useTheme();
  const meta = TIER_META[tier] || { label: tier };
  const dotted = ["paused", "completed", "cancelled"].includes(tier);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5">
      {!dotted && (
        <span
          className="inline-block w-2 h-2 rounded-[2px]"
          style={{ background: tierColor(tier, theme) }}
        />
      )}
      <span className="micro-label">{meta.label}</span>
    </span>
  );
}

/* The "little toggle": the month a not-yet-billing subscription is expected to
   start. A native month input styled to a hairline pill — changing it saves
   the executive assumption; the × clears it back to the delivery-proposed
   default (or to unscheduled). Active rows show their real start instead. */
function StartMonthCell({ row, onSaved }) {
  const [saving, setSaving] = useState(false);

  if (row.tier === "active") {
    return (
      <div>
        <span className="tabular">{formatDate(row.billing_start_date)}</span>
        <p className="text-[10px] text-muted-foreground mt-0.5">billing started</p>
      </div>
    );
  }
  if (OTHER.includes(row.tier)) return <span className="opacity-30">—</span>;

  const save = async (month) => {
    setSaving(true);
    try {
      // Keyed on the project: a row read off the quote has no subscription
      // record yet, and the backend creates it from the quote on this action.
      await apiClient.patch(`/vexos-executive/programs/${row.program_id}/forecast`, {
        forecast_start_month: month,
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the start month");
    }
    setSaving(false);
  };

  const explicit = Boolean(row.forecast_start_month);
  const hint = explicit
    ? "set here"
    : row.effective_start_month
      ? "proposed from delivery"
      : "no start month";

  return (
    <div className={saving ? "opacity-50 pointer-events-none" : ""}>
      <div className="inline-flex items-center gap-1">
        <input
          type="month"
          value={row.effective_start_month || ""}
          onChange={(e) => e.target.value && save(e.target.value)}
          className="h-7 rounded-lg border border-border bg-transparent px-2 text-xs tabular text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label={`Expected start month for ${row.program_name}`}
        />
        {explicit && (
          <button
            onClick={() => save(null)}
            className="p-1 rounded-full text-muted-foreground hover:text-foreground"
            title="Clear — fall back to the proposed date"
          >
            <X className="w-3 h-3" strokeWidth={1.5} />
          </button>
        )}
      </div>
      <p className={`text-[10px] mt-0.5 ${row.start_is_past ? "text-terracotta-600" : "text-muted-foreground"}`}>
        {row.start_is_past ? `${hint} · date passed, forecast assumes now` : hint}
      </p>
    </div>
  );
}

export default function SubscriptionsPanel({ subscriptions, gaps, onRefresh }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => {
    const c = { all: subscriptions.length, other: 0 };
    subscriptions.forEach((s) => {
      c[s.tier] = (c[s.tier] || 0) + 1;
      if (OTHER.includes(s.tier)) c.other += 1;
    });
    return c;
  }, [subscriptions]);

  const rows = useMemo(() => {
    if (filter === "all") return subscriptions;
    if (filter === "other") return subscriptions.filter((s) => OTHER.includes(s.tier));
    return subscriptions.filter((s) => s.tier === filter);
  }, [subscriptions, filter]);

  return (
    <div className="space-y-4">
      {/* Tier subtabs */}
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border text-xs transition-all duration-250 ease-brand ${
              filter === f.key
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
            <span className="tabular opacity-60">{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-2.5"><span className="micro-label">Project</span></th>
              <th className="text-left px-4 py-2.5"><span className="micro-label">Status</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Monthly</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Annual</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Term</span></th>
              <th className="text-left px-4 py-2.5"><span className="micro-label">Starts</span></th>
              <th className="text-right px-4 py-2.5"><span className="micro-label">Collected</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.subscription_id} className="border-b border-border last:border-0 hover:bg-warm-50/50 transition-colors">
                <td className="px-4 py-3 min-w-[220px]">
                  <button
                    onClick={() => navigate(`/programs/${row.program_id}?tab=payments`)}
                    className="text-left group"
                  >
                    <p className="text-foreground group-hover:underline underline-offset-2">
                      {row.program_name || "Untitled project"}
                    </p>
                    <p className="text-xs text-muted-foreground">{row.client_name || "—"}</p>
                  </button>
                </td>
                <td className="px-4 py-3">
                  <TierBadge tier={row.tier} />
                  {row.autopay && (
                    <p className="text-[10px] text-muted-foreground mt-1">autopay on file</p>
                  )}
                  {row.source === "quote" && (
                    <p
                      className="text-[10px] text-muted-foreground mt-1"
                      title="Read from the linked proposal's payment schedule. Setting a start month starts tracking it."
                    >
                      from proposal · not tracked
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular">{formatCurrency(row.monthly_amount)}</td>
                <td className="px-4 py-3 text-right tabular">{formatCurrency(row.annual_value)}</td>
                <td className="px-4 py-3 text-right tabular text-muted-foreground">{row.term_months} mo</td>
                <td className="px-4 py-3">
                  <StartMonthCell row={row} onSaved={onRefresh} />
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="tabular">{formatCurrency(row.paid_total)}</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {row.settled_count} of {row.period_count} months settled
                  </p>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No subscriptions in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Start months on unstarted subscriptions are planning assumptions for the
        forecast — billing itself only starts from the project's payments tab.
      </p>

      <GapsSection gaps={gaps} />
    </div>
  );
}

/* Why the book might be smaller than the business. A VEXOS project with no
   subscription and no monthly on a linked quote contributes nothing to the
   forecast — saying which ones, and why, beats a quietly low total. */
function GapsSection({ gaps }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const noQuote = gaps?.no_quote || [];
  const noMonthly = gaps?.quote_no_monthly || [];
  const total = noQuote.length + noMonthly.length;
  if (!total) return null;

  return (
    <div className="rounded-xl border border-border">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm">
          <AlertCircle className="w-3.5 h-3.5 text-warm-600 flex-shrink-0" strokeWidth={1.5} />
          <span className="text-foreground">
            {total} VEXOS {total === 1 ? "project has" : "projects have"} no recurring revenue on file
          </span>
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
          {open ? "Hide" : "Show"}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-250 ease-brand ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-5 border-t border-border pt-4">
          {noQuote.length > 0 && (
            <div>
              <p className="micro-label mb-1">No proposal linked · {noQuote.length}</p>
              <p className="text-xs text-muted-foreground mb-2.5">
                Nothing to read a monthly fee from. Linking the proposal in Quote
                Links starts its subscription automatically.
              </p>
              <ul className="space-y-1">
                {noQuote.map((p) => (
                  <li key={p.id} className="flex items-baseline justify-between gap-4 text-sm border-b border-border last:border-0 py-1.5">
                    <button
                      onClick={() => navigate(`/programs/${p.id}`)}
                      className="text-left text-foreground hover:underline underline-offset-2"
                    >
                      {(p.program_name || "Untitled project").trim()}
                    </button>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {p.on_horizon ? "On the Horizon" : p.stage}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/sales/quote-links")}
                className="mt-3 inline-flex items-center gap-1.5 h-8 px-4 rounded-full border border-border text-xs text-foreground hover:opacity-88 transition-opacity"
              >
                Open Quote Links
              </button>
            </div>
          )}

          {noMonthly.length > 0 && (
            <div>
              <p className="micro-label mb-1">Proposal has no monthly · {noMonthly.length}</p>
              <p className="text-xs text-muted-foreground mb-2.5">
                A proposal is linked but its payment schedule carries no platform
                fee — either a one-time-only deal, or the fee never made it onto
                the quote.
              </p>
              <ul className="space-y-1">
                {noMonthly.map((p) => (
                  <li key={p.id} className="flex items-baseline justify-between gap-4 text-sm border-b border-border last:border-0 py-1.5">
                    <button
                      onClick={() => navigate(`/programs/${p.id}`)}
                      className="text-left text-foreground hover:underline underline-offset-2"
                    >
                      {(p.program_name || "Untitled project").trim()}
                    </button>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {p.on_horizon ? "On the Horizon" : p.stage}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
